import { Link } from "react-router-dom";
import { ArrowRight, TrendingUp, Building2 } from "lucide-react";
import { SITE } from "@/constants/testIds";

/** Renders the grouped (companies/sectors/industries) typeahead dropdown for CompanySearch. */
export function SearchResultsDropdown({ query, data, active, setActive, onSelectFacet, onSelectResult }) {
  const hasResults = data.reports.length > 0 || data.sectors.length > 0 || data.industries.length > 0;

  return (
    <div
      data-testid={SITE.heroSearchResults}
      className="absolute z-40 mt-2 w-full overflow-hidden rounded-lg border border-kira-border bg-kira-surface shadow-2xl"
    >
      {!hasResults ? (
        <p className="px-4 py-4 text-sm text-kira-textMuted">No published research matches "{query}".</p>
      ) : (
        <ul className="max-h-[400px] overflow-y-auto">
          {data.reports.length > 0 && (
            <li className="px-4 pt-3 pb-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-kira-textMuted/70">
              Companies
            </li>
          )}
          {data.reports.map((r, idx) => {
            const isActive = idx === active;
            return (
              <li key={r.id}>
                <Link
                  to={`/company/${r.slug}`}
                  onMouseEnter={() => setActive(idx)}
                  onClick={onSelectResult}
                  className={`flex items-center justify-between gap-3 px-4 py-2.5 border-b border-kira-border last:border-0 ${isActive ? "bg-white/[0.04]" : "hover:bg-white/[0.03]"}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-white/5 border border-kira-border">
                      <TrendingUp className="h-3.5 w-3.5 text-kira-textMuted" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-white font-medium truncate">
                        {r.company_name}
                        {r.symbol && <span className="ml-2 font-mono text-[10px] text-kira-gold">{r.symbol}</span>}
                      </p>
                      <p className="font-mono text-[11px] text-kira-textMuted truncate">
                        {r.sector}{r.industry ? ` · ${r.industry}` : ""}
                      </p>
                    </div>
                  </div>
                  <span className="font-mono text-xs font-semibold text-kira-gold whitespace-nowrap">
                    KIRA {r.kira_score}
                  </span>
                </Link>
              </li>
            );
          })}

          {data.sectors.length > 0 && (
            <li className="px-4 pt-3 pb-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-kira-textMuted/70">
              Sectors
            </li>
          )}
          {data.sectors.map((s, i) => {
            const idx = data.reports.length + i;
            const isActive = idx === active;
            return (
              <li key={`sec-${s}`}>
                <button
                  type="button"
                  onClick={() => onSelectFacet({ kind: "sector", value: s })}
                  onMouseEnter={() => setActive(idx)}
                  className={`w-full flex items-center justify-between gap-3 px-4 py-2 text-left ${isActive ? "bg-white/[0.04]" : "hover:bg-white/[0.03]"}`}
                >
                  <span className="text-sm text-white">{s}</span>
                  <ArrowRight className="h-3.5 w-3.5 text-kira-textMuted" />
                </button>
              </li>
            );
          })}

          {data.industries.length > 0 && (
            <li className="px-4 pt-3 pb-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-kira-textMuted/70">
              Industries
            </li>
          )}
          {data.industries.map((it, i) => {
            const idx = data.reports.length + data.sectors.length + i;
            const isActive = idx === active;
            return (
              <li key={`ind-${it}`}>
                <button
                  type="button"
                  onClick={() => onSelectFacet({ kind: "industry", value: it })}
                  onMouseEnter={() => setActive(idx)}
                  className={`w-full flex items-center justify-between gap-3 px-4 py-2 text-left ${isActive ? "bg-white/[0.04]" : "hover:bg-white/[0.03]"}`}
                >
                  <span className="flex items-center gap-2 text-sm text-white">
                    <Building2 className="h-3.5 w-3.5 text-kira-textMuted" /> {it}
                  </span>
                  <ArrowRight className="h-3.5 w-3.5 text-kira-textMuted" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
